import Flag, { FlagGroup } from '@atlaskit/flag';
import SuccessIcon from '@atlaskit/icon/glyph/check-circle';
import Error from '@atlaskit/icon/glyph/error';
import Info from '@atlaskit/icon/glyph/info';
import Warning from '@atlaskit/icon/glyph/warning';
import React, { createContext, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { v4 as uuidV4 } from 'uuid';

const initialState = {
    pushFlag: undefined,
};

export const Context = createContext(initialState);

const FLAG_ICON_MAP = {
    success: <SuccessIcon label="success" primaryColor="#00875A" />,
    error: <Error label="error" primaryColor="#DE350B" />,
    info: <Info label="info" primaryColor="#42526E" />,
    warning: <Warning label="warning" primaryColor="#FFC400" />,
};

const Provider = ({ children }) => {
    const [flags, setFlags] = useState([]);

    const dismissFlag = useCallback(() => setFlags((prev) => prev.slice(1)), []);

    const removeFlag = useCallback((id) => setFlags((prev) => prev.filter((flag) => flag.id !== id)), []);

    const timeouts = useRef([]);

    const pushFlag = useCallback(
        (title, message, type = 'info', timeout = 5000, actions) => {
            const newFlagID = uuidV4();
            setFlags((prev) => {
                const time = new Date();
                const notification = {
                    created: time.getTime(),
                    description: message,
                    icon: FLAG_ICON_MAP[type],
                    id: newFlagID,
                    key: time.getTime(),
                    title,
                    actions,
                };

                return [...prev, notification];
            });
            if (timeout > 0) {
                timeouts.current.push(setTimeout(() => removeFlag(newFlagID), timeout));
            }
        },
        [removeFlag],
    );

    useEffect(() => {
        // When the component is about to unmount, clear the timeouts array(even the expired ones!)
        const currentTimeouts = timeouts.current;
        return () => currentTimeouts.forEach(clearTimeout);
    }, []);

    const contextValue = useMemo(
        () => ({
            pushFlag,
        }),
        [pushFlag],
    );

    return (
        <Context.Provider value={contextValue}>
            {children}
            <FlagGroup onDismissed={dismissFlag}>
                {flags.map((flag) => (
                    <Flag key={flag.id} {...flag} />
                ))}
            </FlagGroup>
        </Context.Provider>
    );
};

export function withFlagContext(Component) {
    const WithFlagContext = (props) => (
        <Provider>
            <Component {...props} />
        </Provider>
    );
    return WithFlagContext;
}

export default Provider;
